import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AppWindow, Brain, ScanText, Shield, Image, Video } from 'lucide-react';

export type AIAppType = 'rag' | 'ocr' | 'guardrail' | 'image-gen' | 'video-gen';

export interface AIAppDef {
  id: AIAppType;
  name: string;
  description: string;
  icon: 'Brain' | 'ScanText' | 'Shield' | 'Image' | 'Video';
  stack: string[];
  useCase: string;
}

export const AI_APPS: AIAppDef[] = [
  {
    id: 'rag',
    name: 'RAG Application',
    description: 'Retrieval-Augmented Generation',
    icon: 'Brain',
    stack: ['LangChain', 'ChromaDB', 'vLLM'],
    useCase: 'Document search, knowledge base, semantic search',
  },
  {
    id: 'ocr',
    name: 'OCR',
    description: 'Optical Character Recognition',
    icon: 'ScanText',
    stack: ['Tesseract', 'PaddleOCR', 'FastAPI'],
    useCase: 'Document digitization, text extraction, data entry automation',
  },
  {
    id: 'guardrail',
    name: 'Guardrail',
    description: 'AI safety and content moderation',
    icon: 'Shield',
    stack: ['NeMo Guardrails', 'LlamaGuard', 'FastAPI'],
    useCase: 'Content filtering, prompt injection protection, safety checks',
  },
  {
    id: 'image-gen',
    name: 'Image Generate',
    description: 'Text-to-image generation service',
    icon: 'Image',
    stack: ['Stable Diffusion', 'ComfyUI', 'FastAPI'],
    useCase: 'Creative content, product mockups, design prototypes',
  },
  {
    id: 'video-gen',
    name: 'Video Generate',
    description: 'AI-powered video generation',
    icon: 'Video',
    stack: ['Wan2.1', 'Diffusers', 'FastAPI'],
    useCase: 'Marketing videos, content creation, video synthesis',
  },
];

interface AIAppSelectorProps {
  selectedApp: AIAppType;
  onAppChange: (app: AIAppType) => void;
  disabled?: boolean;
}

const getAppIcon = (icon: AIAppDef['icon']) => {
  switch (icon) {
    case 'Brain':
      return <Brain className="h-5 w-5 text-purple-500" />;
    case 'ScanText':
      return <ScanText className="h-5 w-5 text-blue-500" />;
    case 'Shield':
      return <Shield className="h-5 w-5 text-green-500" />;
    case 'Image':
      return <Image className="h-5 w-5 text-pink-500" />;
    case 'Video':
      return <Video className="h-5 w-5 text-orange-500" />;
    default:
      return null;
  }
};

const AIAppSelector = ({
  selectedApp,
  onAppChange,
  disabled = false,
}: AIAppSelectorProps) => {
  const selectedAppDef = AI_APPS.find((a) => a.id === selectedApp);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Label className="text-base font-medium flex items-center gap-2">
            <AppWindow className="h-4 w-4" />
            Pre-built AI Application
          </Label>
          <p className="text-sm text-muted-foreground">
            Select an AI application to deploy with your instance
          </p>
        </div>
        <Badge variant="secondary">Demo</Badge>
      </div>

      <RadioGroup
        value={selectedApp}
        onValueChange={(v) => onAppChange(v as AIAppType)}
        disabled={disabled}
        className="grid grid-cols-2 gap-3"
      >
        {AI_APPS.map((app) => (
          <Card
            key={app.id}
            className={`cursor-pointer transition-colors ${
              selectedApp === app.id
                ? 'border-primary bg-primary/5'
                : 'hover:border-muted-foreground/50'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => !disabled && onAppChange(app.id)}
          >
            <CardContent className="flex flex-col items-center gap-2 p-4">
              <RadioGroupItem
                value={app.id}
                id={`ai-app-${app.id}`}
                disabled={disabled}
                className="sr-only"
              />
              <div className="flex items-center gap-2">
                {getAppIcon(app.icon)}
                <label
                  htmlFor={`ai-app-${app.id}`}
                  className="font-medium cursor-pointer"
                >
                  {app.name}
                </label>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {app.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </RadioGroup>

      {/* Selected App Details */}
      {selectedAppDef && (
        <Card className="bg-muted/50">
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center gap-2">
              {getAppIcon(selectedAppDef.icon)}
              <span className="font-medium">{selectedAppDef.name}</span>
            </div>
            <div className="space-y-2">
              <div>
                <span className="text-xs text-muted-foreground">Technology Stack</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedAppDef.stack.map((tech) => (
                    <Badge key={tech} variant="outline" className="text-xs">
                      {tech}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">Use Cases</span>
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedAppDef.useCase}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AIAppSelector;
